import { styled } from "@mui/material/styles";
import { Button, CheckIcon, PencilIcon, thinScrollbar } from "@UI";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { answerWordExec, useWordExecCards } from "../../hooks/clientTools/wordExec";
import { useChatComposerStore } from "../../store/chatComposerStore";
import { interruptionKey } from "../utils/cardInterruption";
import { HookCard, HookTile, SettledLine, useCardFocus } from "./HookCard";
const Toggle = styled(Button)({
    alignSelf: "flex-start",
    marginLeft: "calc(-1 * var(--j-btn-px))"
});
const Code = styled("pre")({
    margin: 0,
    maxHeight: 240,
    overflow: "auto",
    ...thinScrollbar,
    padding: "var(--j-sp-2)",
    borderRadius: "var(--j-r-sm)",
    background: "var(--j-panel)",
    border: "1px solid var(--j-line-soft)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    fontSize: "var(--j-fs-xs)",
    lineHeight: "var(--j-lh-xs)",
    whiteSpace: "pre-wrap"
});
export function WordExecCard({ request, isResolved, interrupted }) {
    const { t } = useTranslation();
    const { toolCallId } = request;
    const card = useWordExecCards((state)=>state.cards[toolCallId]);
    const [isCodeShown, setIsCodeShown] = useState(false);
    const setWordApplyMode = useChatComposerStore((s)=>s.setWordApplyMode);
    const { cardRef, hadFocus } = useCardFocus();
    const code = String(request.input.code ?? "");
    const summary = String(request.input.summary ?? "");
    if (card?.status === "done") {
        const { output } = card;
        if ("declined" in output) return /*#__PURE__*/ React.createElement(SettledLine, {
            tone: "neutral",
            hadFocus: hadFocus
        }, t("chat.edit.discarded"));
        if (output.ok) return /*#__PURE__*/ React.createElement(SettledLine, {
            tone: "ok",
            hadFocus: hadFocus
        }, t("chat.exec.ran"));
        return /*#__PURE__*/ React.createElement(SettledLine, {
            tone: "error",
            hadFocus: hadFocus
        }, t("chat.exec.failed", {
            message: output.error.message
        }));
    }
    if (!card) {
        if (isResolved) return null;
        if (interrupted) return /*#__PURE__*/ React.createElement(SettledLine, {
            tone: "neutral"
        }, t(interruptionKey(interrupted)));
        return null;
    }
    const isRunning = card.status === "running";
    return /*#__PURE__*/ React.createElement(HookCard, {
        ref: cardRef,
        "aria-busy": isRunning || undefined
    }, /*#__PURE__*/ React.createElement("div", {
        className: "hook-head"
    }, /*#__PURE__*/ React.createElement(HookTile, {
        "data-tone": "neutral"
    }, /*#__PURE__*/ React.createElement(PencilIcon, {
        size: 16
    })), /*#__PURE__*/ React.createElement("div", {
        className: "hook-text"
    }, /*#__PURE__*/ React.createElement("h4", null, t("chat.exec.title")), summary && /*#__PURE__*/ React.createElement("p", {
        className: "hook-desc"
    }, summary))), /*#__PURE__*/ React.createElement(Toggle, {
        variant: "ghost",
        size: "sm",
        "aria-expanded": isCodeShown,
        onClick: ()=>setIsCodeShown(!isCodeShown)
    }, isCodeShown ? t("chat.exec.hideCode") : t("chat.exec.showCode")), isCodeShown && /*#__PURE__*/ React.createElement(Code, null, code), /*#__PURE__*/ React.createElement("div", {
        className: "hook-actions"
    }, /*#__PURE__*/ React.createElement(Button, {
        variant: "ghost",
        disabled: isRunning,
        onClick: ()=>answerWordExec(toolCallId, false)
    }, t("chat.edit.discard")), /*#__PURE__*/ React.createElement(Button, {
        variant: "ghost",
        disabled: isRunning,
        onClick: ()=>{
            setWordApplyMode("auto");
            answerWordExec(toolCallId, true);
        }
    }, t("chat.exec.alwaysApply")), /*#__PURE__*/ React.createElement(Button, {
        variant: "primary",
        loading: isRunning,
        startIcon: /*#__PURE__*/ React.createElement(CheckIcon, {
            size: 14
        }),
        onClick: ()=>answerWordExec(toolCallId, true)
    }, t("apply"))));
}
